import { useCallback, useRef } from 'react';
import { emailsService } from '../../services/emailsService';
import type { TemplateLanguage } from '../../types/customEmailTemplate';
import type { RelanceEmailDto } from '../../types/customerFollowUp/email';
import { useCustomTemplateDraft } from './useCustomTemplateDraft';

/** How the email tab shows what {@link useCustomTemplateOrchestration} loaded on open. */
export interface InitialDraftHandlers {
    /** A saved draft, with `shown` holding its subject and body in display form. */
    showSaved: (draft: RelanceEmailDto, shown: RelanceEmailDto) => void;
    /** The company template replaced the saved draft; `aiBody` is its AI body, worth keeping for its tone. */
    savedReplaced: (draft: RelanceEmailDto, aiBody: string | null) => void;
    /** No saved draft: the company template was applied. */
    showCustom: (draft: RelanceEmailDto) => void;
    /** Neither a saved draft nor a company template. */
    showDefault: () => Promise<void>;
}

interface Options {
    relanceId: string;
    noAi: boolean;
    /** Shows a subject and body, already in display form. */
    applyDraft: (draft: RelanceEmailDto) => void;
    /** Stops a body stream the template is about to replace. */
    stopStreaming: () => void;
}

/**
 * When the email tab shows the company's custom template: on open it wins over a saved draft that was
 * not written from it, and choosing it stops any body still streaming.
 */
export const useCustomTemplateOrchestration = ({ relanceId, noAi, applyDraft, stopStreaming }: Options) => {
    const applyDraftRef = useRef(applyDraft);
    applyDraftRef.current = applyDraft;
    const applyCurrent = useCallback((draft: RelanceEmailDto) => applyDraftRef.current(draft), []);
    const customDraft = useCustomTemplateDraft(relanceId, applyCurrent);
    const { ensureLoaded, adopt, select, choose } = customDraft;

    // Read through a ref so that toggling No-AI does not rebuild the initial load and re-fetch the draft.
    const noAiRef = useRef(noAi);
    noAiRef.current = noAi;

    const loadInitial = useCallback(async (handlers: InitialDraftHandlers) => {
        await ensureLoaded();
        const draft = await emailsService.getDraft(relanceId);
        if (draft) {
            handlers.showSaved(draft, adopt(draft));
            if (draft.customTemplateLanguage) return;
            if ((await select()).status === 'applied')
                handlers.savedReplaced(draft, !noAiRef.current && draft.bodyTone ? draft.bodyTone : null);
            return;
        }

        const custom = await select();
        if (custom.status === 'applied') handlers.showCustom(custom.draft);
        else await handlers.showDefault();
    }, [relanceId, ensureLoaded, adopt, select]);

    const selectCustom = (language?: TemplateLanguage) => {
        stopStreaming();
        void choose(language);
    };

    const { language, languages, hasTemplate, loading, clear, toSendable } = customDraft;
    return { language, languages, hasTemplate, loading, clear, toSendable, loadInitial, selectCustom };
};
